/**
 * GlobalState.presence 切片与 Worker 数据源接线（14.38）。
 *
 * 形态与边界全在 `~/chat/presenceModel` 文件头（唯一数据源=WS presence 推送流、
 * 条目缺失=unknown 不是离线、重连不清只在登出/切号清）。本文件只做两件事：
 * 把 'chat/presence' 推送并进 map，登出清空。
 *
 * 没有 load/resync：事实源是推送流而非 Worker 内存（session 不持有 map），
 * Worker 崩溃重建后新连接会重新收到此后的变化，旧条目留着照用——和断线重连
 * 是同一种「偏旧」，没有可重拉的快照（contacts/archive 的 resync 不适用）。
 */

import type { GlobalState, GlobalStore } from './global';
import type { AppWorkerHost } from './authState';
import { applyPresence, PRESENCE_EMPTY, type PresenceMap } from '~/chat/presenceModel';

export type PresenceSlice = PresenceMap;

export const PRESENCE_INITIAL: PresenceSlice = PRESENCE_EMPTY;

export interface PresenceConnection {
  /**
   * 把 HTTP 真值回灌进 map（14.38b：friend/info 的 Online 是唯一的在线态接口
   * 真值）。只在已经为别的目的发过该请求时调用，**不为绿点额外发请求**。
   */
  seed(uid: number, online: boolean): void;
  dispose(): void;
}

export function connectPresence(store: GlobalStore, host: AppWorkerHost): PresenceConnection {
  // 登出清空 + 换 uid 清空（旧版 _clearAccountRuntimeState 同口径）：在线态是
  // 「我的好友」的，换账号后一条都不适用。切号链可能从 logged-in(A) 直接到
  // logged-in(B) 不经 logged-out（accountsActions 两相过渡），所以认 uid 不只认状态。
  //
  // ⚠️ 判定写在 syncOwner 里**按需现读 auth**，不只挂在订阅回调上：store 的通知
  // 是帧级批量的（store.ts 语义 1：set 后 get 立即是新值，通知等下一帧），页面
  // 完全可能在本订阅收到「已登录」回调之前就渲染出来并把 friend/info 的种子写
  // 进来——回调再晚一步跑就会把刚写的种子清掉（14.38b 在 iOS WebKit 上实测到，
  // 约 1/3 概率）。写入前先对齐 owner，订阅回调便退化成兜底。
  let currentUid: number | null = null;
  let primed = false;

  function syncOwner(): void {
    const auth = store.get().auth;
    const uid = auth.status === 'logged-in' ? auth.userId : null;
    if (primed && uid === currentUid) return;
    primed = true;
    currentUid = uid;
    if (store.get().presence.size > 0) store.set({ presence: PRESENCE_EMPTY });
  }

  function merge(entries: readonly { uid: number; platforms: readonly string[] }[]): void {
    syncOwner(); // 先认账号再落条目（否则迟到的清空会把本次写入抹掉）
    store.set((prev) => {
      const next = applyPresence(prev.presence, entries);
      return next === prev.presence ? prev : { ...prev, presence: next };
    });
  }

  const offPush = host.onPush('chat/presence', (push) => merge(push.entries));
  const offAuth = store.subscribe((s: GlobalState) => s.auth, syncOwner);

  return {
    seed(uid, online) {
      // 种子用 'unknown' 平台占位（HTTP 只给布尔，没有平台维度）；后续真 presence
      // 帧会带真实平台覆盖它
      merge([{ uid, platforms: online ? ['unknown'] : [] }]);
    },
    dispose() {
      offPush();
      offAuth();
    },
  };
}
